/**
 * Spec 218 PR1 — HTTP client for the Verifactu invoicing endpoints
 * on the relay (web side).
 *
 * Mirrors `mobile/src/sync/invoicing.ts` byte-for-byte at the wire
 * level. The relay (not the web client) holds the Verifacti API
 * key. This file only speaks to `${RELAY_HTTP_URL}/invoices/*`
 * with `x-company-token` for auth — same pattern as photo upload
 * and work sync.
 *
 * Critical: every fiscal payload leaves the device in clear over
 * TLS — by design. AEAT requires plaintext, the E2EE umbrella
 * doesn't apply here. See VERIFACTI_INTEGRATION.md §2.1 + spec 218
 * §1 for the full rationale.
 *
 * Why not reuse the mobile module: it imports `relay.token` from a
 * singleton singleton tied to a React Native socket. The web version
 * accepts the token explicitly so callers (the routes) can pull it
 * from `WebIdentity` without dragging the mobile relay client.
 */

import { RELAY_HTTP_URL } from '../config/env';
import type {
  AeatStatus,
  FiscalProfile,
  Invoice,
  InvoiceDraft,
  InvoiceRecipient,
} from '@cuadrilla/types/invoice';

export class InvoicingError extends Error {
  constructor(public code: string, message: string, public status?: number) {
    super(message);
    this.name = 'InvoicingError';
  }
}

/** Per-call options. The token is required and comes from the
 *  paired `WebIdentity` (`identity.companyToken`). */
export interface InvoicingCtx {
  companyToken: string;
  /** Optional `fetch` override for tests. Defaults to the global. */
  fetcher?: typeof fetch;
}

function authHeaders(ctx: InvoicingCtx): Record<string, string> {
  if (!ctx.companyToken) {
    throw new InvoicingError('NO_TOKEN', 'Missing x-company-token (not paired)');
  }
  return {
    'Content-Type': 'application/json',
    'x-company-token': ctx.companyToken,
  };
}

async function request<T>(
  ctx: InvoicingCtx,
  path: string,
  method: string,
  body?: unknown,
): Promise<T> {
  const url = `${RELAY_HTTP_URL}${path}`;
  const fetcher = ctx.fetcher ?? fetch;
  let res: Response;
  try {
    res = await fetcher(url, {
      method,
      headers: authHeaders(ctx),
      body: body ? JSON.stringify(body) : undefined,
    });
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Network error';
    throw new InvoicingError('NETWORK', message);
  }
  const text = await res.text();
  let parsed: unknown = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      // Non-JSON body — leave as null so the error path below can
      // surface a readable HTTP status without crashing.
    }
  }
  if (res.status === 503) {
    throw new InvoicingError('DISABLED', 'Invoicing not enabled on relay', 503);
  }
  if (!res.ok) {
    const obj = (parsed ?? {}) as { error?: string; detail?: string };
    const base = obj.error || `HTTP ${res.status}`;
    const message = obj.detail ? `${base}: ${obj.detail}` : base;
    throw new InvoicingError('API_ERROR', message, res.status);
  }
  return parsed as T;
}

// ── Fiscal profile ────────────────────────────────────────────────

export interface FiscalProfileInput {
  nif: string;
  legalName: string;
  address: string;
  postalCode: string;
  city: string;
  province: string;
  iban?: string;
}

/** Returns null when the team has no fiscal profile yet. Throws on
 *  any other error (network, auth, 5xx). */
export async function getFiscalProfile(ctx: InvoicingCtx): Promise<FiscalProfile | null> {
  try {
    const r = await request<{
      id: string;
      nif: string;
      legalName: string;
      address: string;
      postalCode: string;
      city: string;
      province: string;
      iban?: string;
      environment: 'test' | 'production';
      representationSigned: boolean;
      verifactiNifId?: string;
      createdAt: string;
      updatedAt: string;
    }>(ctx, '/invoices/fiscal-profile', 'GET');
    return {
      id: r.id,
      nif: r.nif,
      legalName: r.legalName,
      address: r.address,
      postalCode: r.postalCode,
      city: r.city,
      province: r.province,
      iban: r.iban ?? undefined,
      environment: r.environment,
      representationSigned: r.representationSigned,
      verifactiNifId: r.verifactiNifId ?? undefined,
      createdAt: new Date(r.createdAt).getTime(),
      updatedAt: new Date(r.updatedAt).getTime(),
    };
  } catch (err) {
    if (err instanceof InvoicingError && err.status === 404) return null;
    throw err;
  }
}

export async function saveFiscalProfile(
  ctx: InvoicingCtx,
  input: FiscalProfileInput,
): Promise<{ id: string }> {
  return request<{ id: string }>(ctx, '/invoices/fiscal-profile', 'POST', input);
}

// ── Invoice emission ──────────────────────────────────────────────

export interface EmitResponse {
  id: string;
  verifactiUuid?: string;
  qrBase64?: string;
  qrUrl?: string;
  hash?: string;
  aeatStatus: AeatStatus;
  total: number;
}

export async function emitInvoice(ctx: InvoicingCtx, draft: InvoiceDraft): Promise<EmitResponse> {
  return request<EmitResponse>(ctx, '/invoices/emit', 'POST', { draft });
}

// ── Listing / detail / cancel ─────────────────────────────────────

export interface InvoiceSummary {
  id: string;
  series: string;
  number: string;
  issueDate: string;
  type: string;
  total: number;
  recipientName?: string;
  recipientNif?: string;
  aeatStatus: AeatStatus;
  cancelled: boolean;
  qrUrl?: string;
  createdAt: string;
}

export async function listInvoices(
  ctx: InvoicingCtx,
  filter?: { status?: AeatStatus },
): Promise<InvoiceSummary[]> {
  const qs = filter?.status ? `?status=${filter.status}` : '';
  return request<InvoiceSummary[]>(ctx, `/invoices${qs}`, 'GET');
}

function materializeInvoice(
  draft: InvoiceDraft,
  r: Record<string, unknown>,
  recipient: InvoiceRecipient,
): Invoice & { draft: InvoiceDraft } {
  return {
    // ── InvoiceDraft fields ───────────────────────────────────────────
    series: draft.series,
    number: draft.number,
    issueDate: draft.issueDate,
    operationDate: draft.operationDate,
    type: draft.type,
    description: draft.description,
    recipient,
    lines: draft.lines,
    workId: draft.workId,
    rectification: draft.rectification,
    // ── Invoice-only fields from relay response ───────────────────────
    id: r.id as string,
    emittedAt: r.emittedAt ? new Date(r.emittedAt as string).getTime() : Date.now(),
    total: r.total as number,
    totalBase: (r.totalBase as number | undefined) ?? 0,
    totalVat: (r.totalVat as number | undefined) ?? 0,
    verifactiUuid: r.verifactiUuid as string | undefined,
    qrBase64: r.qrBase64 as string | undefined,
    qrUrl: r.qrUrl as string | undefined,
    hash: r.hash as string | undefined,
    aeatStatus: r.aeatStatus as AeatStatus,
    aeatMessage: r.aeatMessage as string | undefined,
    cancelled: r.cancelled as boolean | undefined,
    cancelledAt: r.cancelledAt ? new Date(r.cancelledAt as string).getTime() : undefined,
    rectifiedByInvoiceId: r.rectifiedByInvoiceId as string | undefined,
    rectifiedBy: r.rectifiedBy
      ? (() => {
          const rb = r.rectifiedBy as { id: string; series: string; number: string };
          return { id: rb.id, series: rb.series, number: rb.number };
        })()
      : undefined,
    draft,
  };
}

export async function getInvoice(
  ctx: InvoicingCtx,
  id: string,
): Promise<Invoice & { draft: InvoiceDraft }> {
  const r = await request<Record<string, unknown>>(ctx, `/invoices/${id}`, 'GET');
  const draft = (r as { draft?: InvoiceDraft }).draft;
  const recipient: InvoiceRecipient =
    draft?.recipient ??
    (r.recipientName || r.recipientNif || r.recipientEmail
      ? {
          kind: 'none' as const,
          name: r.recipientName as string | undefined,
          nif: r.recipientNif as string | undefined,
          email: r.recipientEmail as string | undefined,
        }
      : { kind: 'none' });
  if (!draft) {
    return { ...r, recipient } as unknown as Invoice & { draft: InvoiceDraft };
  }
  return materializeInvoice(draft, r, recipient);
}

export async function cancelInvoice(ctx: InvoicingCtx, id: string): Promise<void> {
  await request(ctx, `/invoices/${id}/cancel`, 'POST');
}

/** Spec 218 PR2 — discoverable feature flag. The relay exposes
 *  `GET /features` returning `{ verifacti: boolean }`; the web
 *  uses it to gate the entire Facturación group in the sidebar.
 *  Returns false on any error so a misconfigured relay never lets
 *  the route render with no backend behind it. */
export async function isVerifactiEnabled(ctx: InvoicingCtx): Promise<boolean> {
  try {
    const r = await request<{ verifacti?: boolean }>(ctx, '/features', 'GET');
    return r?.verifacti === true;
  } catch (err) {
    if (err instanceof InvoicingError && (err.status === 404 || err.status === 503)) {
      return false;
    }
    return false;
  }
}
